import type { MouseEvent as ReactMouseEvent, TouchEvent as ReactTouchEvent } from 'react';
import { Checkbox, IconButton } from '@vkontakte/vkui';
import {
  Icon24Attach, Icon24CalendarOutline, Icon24CheckCircleOutline,
  Icon24MoreHorizontal, Icon24Note,
} from '@vkontakte/icons';
import { CatBadge, catTone } from '../../ui/CatBadge';
import type { Task, TaskCategory } from '../../types';
import { fmtDate, STATUS_META, type TaskStatus } from './taskUtils';

export type TaskDragAttrs = {
  onTouchStart: (e: ReactTouchEvent<HTMLDivElement>) => void;
  onTouchMove: (e: ReactTouchEvent<HTMLDivElement>) => void;
  onTouchEnd: () => void;
  onMouseDown: (e: ReactMouseEvent<HTMLDivElement>) => void;
  onMouseMove: (e: ReactMouseEvent<HTMLDivElement>) => void;
  onMouseUp: () => void;
};

type Props = {
  task: Task;
  category?: TaskCategory;
  status: TaskStatus;
  assignee?: string;
  subtasks?: { done: number; total: number } | null;
  attachmentCount?: number | null;
  expanded?: boolean;
  dragging?: boolean;
  isOver?: boolean;
  onToggle: () => void;
  onOpen: () => void;
  onMenu: (anchor: Element) => void;
  onExpand: () => void;
  dragAttrs: TaskDragAttrs;
};

export function TaskCard(p: Props) {
  const { task: t } = p;
  const tone = p.category ? catTone(p.category.icon_id) : 'rose';
  const meta = STATUS_META[p.status];
  return (
    <div
      className={`tp-card tp-card--${tone}${p.dragging ? ' tp-card--dragging' : ''}${p.isOver ? ' tp-card--over' : ''}${t.is_complete ? ' tp-card--done' : ''}`}
      data-task-id={t.id}
      style={{
        WebkitTouchCallout: 'none',
        userSelect: 'none',
      }}
      {...p.dragAttrs}
    >
      <div className="tp-card__strip" />
      <div
        className="tp-card__check"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
      >
        <Checkbox checked={t.is_complete} onChange={p.onToggle} />
      </div>

      <div className="tp-card__main" onClick={p.onOpen}>
        <div className="tp-card__title">{t.name}</div>
        <div className="tp-card__meta">
          <CatBadge iconId={p.category?.icon_id} size={20} />
          <span>{p.category ? p.category.name : 'Без категории'}</span>
        </div>
        {t.due_date && (
          <div className={`tp-card__due${p.status === 'overdue' ? ' tp-card__due--overdue' : ''}`}>
            <Icon24CalendarOutline width={16} height={16} />
            <span>Срок {fmtDate(t.due_date)}</span>
          </div>
        )}
        <div className="tp-card__badges">
          <span className={`tp-chip ${meta.cls}`}>{meta.label}</span>
          {t.note ? (
            <span className="tp-ind" title="Есть заметка">
              <Icon24Note width={16} height={16} />
            </span>
          ) : null}
          {p.attachmentCount ? (
            <span className="tp-ind" title="Вложения">
              <Icon24Attach width={16} height={16} />
              <span>{p.attachmentCount}</span>
            </span>
          ) : null}
          <button type="button" className={`tp-ind tp-ind--btn${p.expanded ? ' tp-ind--active' : ''}`} onClick={(e) => { e.stopPropagation(); p.onExpand(); }}>
            <Icon24CheckCircleOutline width={16} height={16} />
            <span>{p.subtasks ? `Подзадачи ${p.subtasks.done}/${p.subtasks.total}` : 'Подзадачи'}</span>
          </button>
          {p.assignee ? <span className="tp-ind tp-ind--text">{p.assignee}</span> : null}
        </div>
      </div>

      <div
        className="tp-card__actions"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
      >
        <IconButton label="Дополнительные действия" onClick={(e) => p.onMenu(e.currentTarget)}>
          <Icon24MoreHorizontal />
        </IconButton>
      </div>
    </div>
  );
}
